import { Box, Button, FileButton, Group, Input, SimpleGrid, Stepper, Table, TextInput } from '@mantine/core';

import { Flex, Paper, Text } from '@mantine/core';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { IconUpload } from '@tabler/icons-react';
import { useCsvPreview } from './hooks/useCsvPreview';
import HeaderIndexSection from './section/headerIndexSection';
import TemplateNameSection from './section/templateNameSection';
import { useMutation, useQuery } from '@tanstack/react-query';
import ColumnSection from './section/columnSection';
import { AxiosError } from 'axios';
import { handleError } from '../../../../../ui/err/handleError';
import { AxiosCommonError } from '../../../../../../shared/types';
import { buildSearchString } from '../../utils/buildSearchString';
import { aiAgentApis } from '../../../../../../shared/apis/ai-agent/apis';

export default function ColumnMapping() {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const navigate = useNavigate();
  const params = useParams();
  const { companyId, templateId } = params;
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || '';
  const { previewData, usedEncoding, parseFile } = useCsvPreview();
  const [headerIndex, setHeaderIndex] = useState<string | null>(null);
  const [templateForm, setTemplateForm] = useState<{
    name: { value: string; error: string };
    description: { value: string; error: string };
  }>({
    name: {
      value: '',
      error: '',
    },
    description: {
      value: '',
      error: '',
    },
  });
  const [active, setActive] = useState(0);
  const [headerSettingSaved, setHeaderSettingSaved] = useState(false);

  const prevStep = () => setActive((current) => (current > 0 ? current - 1 : current));

  const { mutate: createTemplate } = useMutation(aiAgentApis.template.createAsCsTemplate);
  const { mutate: createAsCsTemplateField } = useMutation(aiAgentApis.template.createAsCsTemplateField);

  const onChangeFile = (file: File | null) => {
    setUploadedFile(file);
    if (file) {
      parseFile(file, 1);
      setHeaderIndex('1');
    }
  };

  const { data } = useQuery({
    queryKey: ['getAsCsTemplate', templateId],
    queryFn: () => aiAgentApis.template.getAsCsTemplate(Number(templateId)),
    enabled: !!templateId,
  });

  // 파일 크기를 읽기 쉬운 형식으로 변환
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const onClickToHeaderSettingStep = () => {
    setActive(1);
  };
  const onClickToColumnMappingStep = () => {
    if (headerSettingSaved) {
      setActive(2);
      return;
    }
    createTemplate(
      {
        name: templateForm.name.value,
        description: templateForm.description.value,
        companyId: Number(companyId),
        headerIndex: Number(headerIndex),
        encoding: usedEncoding || '',
      },
      {
        onSuccess: (data) => {
          const templateId = data.data.data.id;
          const filterData = previewData[0]
            .filter((item) => item !== '')
            .map((item, index) => {
              return {
                name: item,
                description: '',
                fieldOrder: index + 1,
              };
            });
          createAsCsTemplateField(
            {
              templateId: templateId,
              companyId: Number(companyId),
              field: filterData,
            },
            {
              onSuccess: () => {
                setHeaderSettingSaved(true);
                navigate(
                  `/admin/management/aiagent/company/${companyId}/template/register/${templateId}${buildSearchString({
                    companyName,
                  })}`,
                );
                setActive(2);
              },
              onError: (error) => {
                handleError(error as AxiosError<AxiosCommonError>);
              },
            },
          );
        },
        onError: (error) => {
          handleError(error as AxiosError<AxiosCommonError>);
        },
      },
    );
  };

  return (
    <div>
      <Stepper active={active} my="md">
        <Stepper.Step label="템플릿 설정">
          <Flex justify="flex-end" align="center" my="md">
            <FileButton onChange={onChangeFile} accept=".csv">
              {(props) => (
                <Button {...props} leftSection={<IconUpload size={16} />}>
                  파일 업로드
                </Button>
              )}
            </FileButton>
          </Flex>
          <Paper withBorder p="md" mb="md">
            <SimpleGrid cols={2}>
              <Input.Wrapper label="파일명" error={templateForm.name.error}>
                <TextInput value={uploadedFile ? uploadedFile.name : '파일을 업로드해주세요.'} disabled />
              </Input.Wrapper>
              <Input.Wrapper label="파일 크기" error={templateForm.name.error}>
                <TextInput value={uploadedFile ? formatFileSize(uploadedFile.size) : '0 Bytes'} disabled />
              </Input.Wrapper>
              <TemplateNameSection templateForm={templateForm} setTemplateForm={setTemplateForm} />
            </SimpleGrid>
          </Paper>
          <Group justify="center" mt="xl">
            <Button variant="default" onClick={() => navigate(-1)}>
              뒤로가기
            </Button>
            <Button
              disabled={!templateForm.name.value || !templateForm.description.value || !uploadedFile}
              onClick={onClickToHeaderSettingStep}
            >
              다음
            </Button>
          </Group>
        </Stepper.Step>
        <Stepper.Step label="헤더 행 설정">
          <HeaderIndexSection
            parseFile={parseFile}
            file={uploadedFile}
            headerIndex={headerIndex}
            setHeaderIndex={setHeaderIndex}
          />

          {previewData.length > 0 && (
            <Box my="md">
              <Text fw={500}>파일 미리보기(처음 5행)</Text>
              <Table.ScrollContainer minWidth={700}>
                <Table withColumnBorders style={{ tableLayout: 'auto', width: 'max-content', minWidth: '100%' }}>
                  <Table.Tbody>
                    {previewData.map((row, rowIndex) => (
                      <Table.Tr
                        key={rowIndex}
                        style={{
                          background: rowIndex === 0 ? 'rgba(0, 123, 255, 0.1)' : 'none',
                        }}
                      >
                        {row.map((cell, cellIndex) => (
                          <Table.Td key={cellIndex} style={{ whiteSpace: 'nowrap', minWidth: 120 }}>
                            {cell}
                          </Table.Td>
                        ))}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Box>
          )}
          <Group justify="center" mt="xl">
            {!headerSettingSaved && (
              <Button variant="default" onClick={prevStep}>
                이전
              </Button>
            )}
            <Button onClick={onClickToColumnMappingStep}>{headerSettingSaved ? '다음' : '저장'}</Button>
          </Group>
        </Stepper.Step>
        <Stepper.Step label="컬럼 맵핑">
          <Flex gap="md" direction="column">
            <ColumnSection fields={data?.data?.data?.field || []} prevStep={prevStep} />
          </Flex>
        </Stepper.Step>
      </Stepper>
    </div>
  );
}
